import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { IUploadedImage } from 'libs/hooks/io/useUploadImage';
import { EUploadResolution } from 'libs/utils/logic/operations/fileOperations';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import { normalizeLng } from 'libs/utils/world/worldUtils';
import { useContext } from 'react';
import { IImageV2WorldMarker } from 'react-give-me-map/dist/utils/world/worldTypes';
import { CtxEdit } from 'session/ctxEdit';

export const useImageCreator = () => {
    const worldMarker = useWorldMarker();
    const ctxEdit = useContext(CtxEdit);

    const map = ctxEdit.mapRef.current;

    function createImageMarkers(
        uploaded: IUploadedImage[],
        lat: number,
        lng: number,
        zoom: number,
        lngOffset: number
    ) {
        console.log('Start creating', uploaded, lat, lng, zoom);
        uploaded.forEach((upload, uploadIndex) => {
            const id = Math.random().toString();
            const img = new Image();
            img.src =
                upload?.data?.imageUrls[EUploadResolution.ULTRA] ??
                upload?.data?.imageUrls[EUploadResolution.BIG];
            img.onload = (): void => {
                const imageUrl = upload?.data?.imageUrls[EUploadResolution.ULTRA];

                map.loadImage(imageUrl, (error, image) => {
                    const finalWidth = 200;
                    const finalHeight = finalWidth * (image.height / image.width);

                    const cTopLeft = ctxEdit.mapRef?.current?.getMap().project([lng, lat]);
                    const cTopRight = { x: cTopLeft.x + finalWidth, y: cTopLeft.y };
                    const cBottomRight = {
                        x: cTopLeft.x + finalWidth,
                        y: cTopLeft.y + finalHeight
                    };
                    const cBottomLeft = { x: cTopLeft.x, y: cTopLeft.y + finalHeight };

                    const topLeft = ctxEdit.mapRef?.current
                        ?.getMap()
                        .unproject([cTopLeft.x, cTopLeft.y]);
                    const topRight = ctxEdit.mapRef?.current
                        ?.getMap()
                        .unproject([cTopRight.x, cTopRight.y]);
                    const bottomRight = ctxEdit.mapRef?.current
                        ?.getMap()
                        .unproject([cBottomRight.x, cBottomRight.y]);
                    const bottomLeft = ctxEdit.mapRef?.current
                        ?.getMap()
                        .unproject([cBottomLeft.x, cBottomLeft.y]);

                    const imageMarker: IImageV2WorldMarker = {
                        id: id,
                        lat: lat,
                        lng: normalizeLng(lng + uploadIndex * lngOffset),
                        elementType: 'v2/image' as const,
                        elementData: {
                            imageUrl,
                            coordinates: [
                                { lat: topLeft.lat, lng: topLeft.lng },
                                { lat: topRight.lat, lng: topRight.lng },
                                { lat: bottomRight.lat, lng: bottomRight.lng },
                                { lat: bottomLeft.lat, lng: bottomLeft.lng }
                            ],
                            nonRotatedCoordinates: [
                                { lat: topLeft.lat, lng: topLeft.lng },
                                { lat: topRight.lat, lng: topRight.lng },
                                { lat: bottomRight.lat, lng: bottomRight.lng },
                                { lat: bottomLeft.lat, lng: bottomLeft.lng }
                            ],
                            generatedBackgroundColor: null,
                            generatedImageUrl: null,
                            generatedEmoji: null,
                            generatedIconColor: null,
                            generatedIconUrl: null,
                            rotation: 0,
                            opacity: 1,
                            flipHorizontal: false,
                            flipVertical: false
                        },
                        scale: 1,
                        visible: true,
                        scalable: true,
                        selectable: true,
                        isExternalPoint: false,
                        rotate: 0,
                        order: worldMarker.findHighestOrder() + 1
                    };
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ctxEdit.addWorldMarker(imageMarker as any);

                    if (error) throw error;
                    if (!map.hasImage(imageUrl)) map.addImage(imageUrl, image, { sdf: false });
                });
            };
        });
        ctxEdit.setWorldAction(EWorldAction.SELECT);
    }

    return {
        createImageMarkers
    };
};
